import React, { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import '../styles/userlist.css';
import { Icon } from '@iconify/react';
import { Link, useLocation, useHistory } from 'react-router-dom';

import '../styles/general.css';
import { useAuth } from '../core/AuthContext';
import { useSemester } from '../core/SemesterContext';
import NavHeader from "./NavHeader";
import Avatar from './Avatar';
import useDepartmentFilter from './hooks/useDepartmentFilter';
import useMembershipFilter from './hooks/useMembershipFilter';
import usePagination from './hooks/usePagination';
import useUsers from './hooks/useUsers';
import LoadingSpinner from './LoadingSpinner';
import { hasAnyPermission, hasPermission } from '../core/utils/permissionUtils';
import {
    getDepartmentColor,
    getDepartmentIcon,
    getDepartmentsForContext,
    getDepartmentId,
    userHasDepartment,
    resolveDepartmentFilterValues,
    getDepartmentName,
} from '../core/constants/departments';
import { useDepartments } from '../core/DepartmentsContext';

const membershipIcons = {
    'Newbie': <Icon icon="streamline:rock-and-roll-hand-remix" />,
    'Junior': <Icon icon="bi:emoji-sunglasses" />,
    'Senior': <Icon icon="mdi:emoticon-devil-outline" />,
    'Intern': <Icon icon="mdi:school-outline" />,
    'Alumni': <Icon icon="tdesign:wave-bye" />,
    'Unbound': <Icon icon="mdi:account-off-outline" />,
}

const membershipColors = {
    'Newbie':  "#b87333",
    'Junior':  "#818181ff",
    'Senior':  "#c9a227",
    'Intern':  "#3f7cac",
    'Alumni':  "#2F2F2F",
    'Unbound': "#9c1f1f",
};

const OFF_ROSTER_MEMBERSHIPS = ['Unbound', 'Alumni'];

const availableMemberships = [
    "Newbie",
    "Junior",
    "Senior",
    "Intern",
    "Unbound",
    "Alumni",
];

function isOffRosterMembership(membershipType) {
    return OFF_ROSTER_MEMBERSHIPS.includes(membershipType);
}

function userMatchesRosterFilters(user, { searchQuery, selectedDepartments, selectedMemberships }) {
    const removeAccents = (str) => str?.normalize('NFD').replace(/\p{Diacritic}/gu, '') || '';
    const hasAccent = (str) => /[^\x20-\x7E]/.test(str);

    const searchMatches = (name, nickname, searchTerm) => {
        if (!searchTerm) return true;

        if (hasAccent(searchTerm)) {
            const search = searchTerm.toLowerCase();
            return (name?.toLowerCase().includes(search)) || (nickname?.toLowerCase().includes(search));
        }

        const normalizedSearch = removeAccents(searchTerm).toLowerCase();
        const normalizedName = removeAccents(name).toLowerCase();
        const normalizedNick = removeAccents(nickname).toLowerCase();
        return normalizedName.includes(normalizedSearch) || normalizedNick.includes(normalizedSearch);
    };

    const matchesSearch = searchMatches(user.fullName, user.nickname, searchQuery);
    const matchesDepartment = selectedDepartments.length === 0 ||
        (user.departments || []).some((dep) =>
            selectedDepartments.some((filterDep) => userHasDepartment([dep], filterDep))
        );
    const matchesMembership = selectedMemberships.length === 0 ||
        selectedMemberships.includes(user.membershipType);

    return matchesSearch && matchesDepartment && matchesMembership;
}

// user.membershipType is global to the user (Newbie/Junior/Senior/Intern/
// Alumni/Unbound), also used elsewhere (RBAC, voting). Unbound/Alumni are
// loaded separately and shown in dedicated sections below the active list.
const UserListItem = memo(({ user, catalog }) => {
    const membershipType = user.membershipType || 'Newbie';
    const isSuspended = !!user.suspension?.isSuspended;
    const isLeft = membershipType === 'Unbound';

    return (
        <li className={`${isSuspended ? "suspended-user" : ""} ${isLeft ? "left-user" : ""} ${
        isToday(new Date(user.birthDate)) ? "birthday-highlight" : ""
        }`}>
        <Link
          to={`/users/${user._id}`}
          className="user-list-item"
          style={{
            '--department-color':
            getDepartmentColor(user.departments?.[0], catalog) }}
        >
          <div className='user-item-info'>
              <div className="user-item-avatar-wrapper">
                <Avatar
                  user={user}
                  style={{ border: '2px solid rgba(0,0,0,0.6)' }}
                />
              </div>
            <p className='user-item-name'>
                        {user.firstLastName ? user.firstLastName : user.fullName} {user.nickname ? `"${user.nickname}"` : ''}
                    </p>
                    <p className="user-item-birthday">
                        <Icon icon="line-md:cake" className="cake-icon" />
                        <span className="birthday-text">{new Date(user.birthDate).toLocaleDateString()}</span>
                    </p>
                    <div className='user-item-status'>
                        <p className='user-item-position'>{user.position} · {membershipType}</p>
                    </div>
                    <p className='user-item-departments'>
                      {user.departments.map((department, index) => (
                        <span key={index} className='user-item-department'
                          style={{ '--department-color': getDepartmentColor(department, catalog)}}>
                            <div className='dep-icon'>{getDepartmentIcon(department, {}, catalog)}</div>
                            <div className='dep-name'>{getDepartmentName(department)}</div>
                            </span>
                        ))}
                    </p>
                </div>
            </Link>
        </li>
    );
});

const UsersListSection = memo(({
    loading,
    hasUsers,
    paginatedUsers,
    currentPage,
    totalPages,
    goToNextPage,
    goToPreviousPage,
    totalActiveCount,
    catalog,
    emptyMessage = 'No users match your search criteria',
}) => {
    return (
        <div className='user-list-main-section'>
            {loading && !hasUsers ? (
                <LoadingSpinner message="Loading users" size="sm" />
            ) : (
                <>
                    <ul className="users-list">
                        {paginatedUsers.length > 0 ? (
                            paginatedUsers.map((user) => (
                                <UserListItem key={user._id} user={user} catalog={catalog} />
                            ))
                        ) : (
                            <li className="no-results" style={{ textAlign: 'center', padding: '20px' }}>
                                {emptyMessage}
                            </li>
                        )}
                    </ul>
                    <div className="pagination-controls" style={{
                        display: 'flex',
                        justifyContent: 'center',
                        margin: '20px 0'
                    }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexWrap: "wrap",
                          gap: "10px",
                          marginTop: "10px",
                        }}
                      >
                        <button
                          className="button"
                          onClick={goToPreviousPage}
                          disabled={currentPage === 1}
                          style={{
                            opacity: currentPage === 1 ? 0.5 : 1,
                            margin: "0",
                            maxWidth: "100px",
                            height: "20px",
                          }}
                        >
                          <i className="uil uil-angle-left-b"></i> Previous
                        </button>

                        <span
                          style={{
                            padding: "8px 16px",
                            borderRadius: "20px",
                            background: "rgba(255, 255, 255, 0.15)",
                            fontSize: "14px",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            lineHeight: "20px",
                          }}
                        >
                          Page {currentPage} of {totalPages || 1}
                        </span>

                        <button
                          className="button"
                          onClick={goToNextPage}
                          disabled={currentPage === totalPages || totalActiveCount === 0}
                          style={{
                            opacity:
                              currentPage === totalPages || totalActiveCount === 0 ? 0.5 : 1,
                            margin: "0",
                            maxWidth: "100px",
                            height: "20px",
                          }}
                        >
                          Next <i className="uil uil-angle-right-b"></i>
                        </button>
                      </div>
                    </div>
                </>
            )}
        </div>
    );
});

const UsersListGroupSection = memo(({ title, icon, users, catalog, defaultExpanded = false }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    return (
        <div className="user-list-group-section">
            <button
                type="button"
                className="user-list-group-header"
                onClick={() => setExpanded((prev) => !prev)}
            >
                <Icon icon={icon} />
                <span className="user-list-group-title">{title}</span>
                <span className="filter-count">({users.length})</span>
                <Icon icon={expanded ? "mdi:chevron-up" : "mdi:chevron-down"} className="chevron-icon" />
            </button>
            {expanded && (
                users.length > 0 ? (
                    <ul className="users-list">
                        {users.map((user) => (
                            <UserListItem key={user._id} user={user} catalog={catalog} />
                        ))}
                    </ul>
                ) : (
                    <p className="user-list-group-empty">Nobody here right now.</p>
                )
            )}
        </div>
    );
});

function isToday(date) {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth()
  );
}

const sortByName = (list) => {
    return [...list].sort((a, b) => {
        const nameA = a.fullName || '';
        const nameB = b.fullName || '';
        return nameA.localeCompare(nameB);
    });
};

function UsersList() {
    const [usersPerPage] = useState(20);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredUsers, setFilteredUsers] = useState([]);
    const searchInputRef = useRef(null);
    const { departments: catalog } = useDepartments();
    const filterDepartments = useMemo(
        () => getDepartmentsForContext('filters', catalog),
        [catalog]
    );

    const location = useLocation();
    const history = useHistory();

    const [unboundUsers, setUnboundUsers] = useState([]);
    const [unboundLoading, setUnboundLoading] = useState(false);
    const [alumniUsers, setAlumniUsers] = useState([]);
    const [alumniLoading, setAlumniLoading] = useState(false);

    const {
        users,
        loading,
        error,
        semesterUsersLoaded,
        loadUnboundUsers,
        loadAlumniUsers,
    } = useUsers();

    // Split the search/department/membership-filtered roster into the active
    // list and suspended members. Unbound/Alumni are not part of this roster;
    // they load only when their membership filter is selected.
    const activeUsers = useMemo(() => {
        return filteredUsers.filter((user) =>
            !isOffRosterMembership(user.membershipType) && !user.suspension?.isSuspended
        );
    }, [filteredUsers]);

    const suspendedUsers = useMemo(() => {
        return filteredUsers.filter((user) =>
            !isOffRosterMembership(user.membershipType) && user.suspension?.isSuspended
        );
    }, [filteredUsers]);

    const sortedActiveUsers = useMemo(() => sortByName(activeUsers), [activeUsers]);
    const sortedSuspendedUsers = useMemo(() => sortByName(suspendedUsers), [suspendedUsers]);

    // Pagination only applies to the active list; the suspended/left sections
    // are expected to be much smaller and are shown in full once expanded.
    const totalActiveUsers = sortedActiveUsers.length;
    const { currentPage, setCurrentPage, totalPages, goToNextPage, goToPreviousPage } = usePagination(usersPerPage, totalActiveUsers);

    const { selectedDepartments, handleDepartmentChange, setSelectedDepartments } = useDepartmentFilter(setCurrentPage);

    const { selectedMemberships, handleMembershipChange, setSelectedMemberships } = useMembershipFilter(setCurrentPage);


    const authContext = useAuth();
    const contextUser = authContext.user;

    const semesterContext = useSemester();
    // currentSemester is the Semester selected not the current active semester
    const semester = semesterContext.currentSemester;
    const searchingUnbound = selectedMemberships.includes('Unbound');
    const searchingAlumni = selectedMemberships.includes('Alumni');
    const onlyOffRosterFilters =
        selectedMemberships.length > 0
        && selectedMemberships.every((mem) => OFF_ROSTER_MEMBERSHIPS.includes(mem));

    // Unbound/Alumni stay off the default roster. They load only when selected.
    useEffect(() => {
        if (!searchingUnbound || !semester?._id) {
            setUnboundUsers([]);
            setUnboundLoading(false);
            return undefined;
        }

        let cancelled = false;
        setUnboundLoading(true);
        loadUnboundUsers()
            .then((rows) => {
                if (!cancelled) setUnboundUsers(rows);
            })
            .catch(() => {
                if (!cancelled) setUnboundUsers([]);
            })
            .finally(() => {
                if (!cancelled) setUnboundLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [searchingUnbound, semester?._id, loadUnboundUsers]);

    useEffect(() => {
        if (!searchingAlumni || !semester?._id) {
            setAlumniUsers([]);
            setAlumniLoading(false);
            return undefined;
        }

        let cancelled = false;
        setAlumniLoading(true);
        loadAlumniUsers()
            .then((rows) => {
                if (!cancelled) setAlumniUsers(rows);
            })
            .catch(() => {
                if (!cancelled) setAlumniUsers([]);
            })
            .finally(() => {
                if (!cancelled) setAlumniLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [searchingAlumni, semester?._id, loadAlumniUsers]);

    const filterFormerMemberRows = useCallback(
        (rows) => rows.filter((user) =>
            userMatchesRosterFilters(user, {
                searchQuery,
                selectedDepartments,
                selectedMemberships,
            })
        ),
        [searchQuery, selectedDepartments, selectedMemberships],
    );

    const leftUsers = useMemo(() => {
        if (!searchingUnbound) return [];
        return filterFormerMemberRows(unboundUsers);
    }, [searchingUnbound, unboundUsers, filterFormerMemberRows]);

    const sortedLeftUsers = useMemo(() => sortByName(leftUsers), [leftUsers]);

    const alumniListUsers = useMemo(() => {
        if (!searchingAlumni) return [];
        return filterFormerMemberRows(alumniUsers);
    }, [searchingAlumni, alumniUsers, filterFormerMemberRows]);

    const sortedAlumniUsers = useMemo(() => sortByName(alumniListUsers), [alumniListUsers]);

    // Filter-button counts reflect only currently active (non-suspended,
    // non-left) volunteers, so they describe the "general volunteer list".
    const activeBaseUsers = useMemo(() => {
        return users.filter((user) =>
            !isOffRosterMembership(user.membershipType) && !user.suspension?.isSuspended
        );
    }, [users]);

    const departmentCounts = useMemo(() => {
      const counts = {};
      filterDepartments.forEach((dept) => {
        const key = getDepartmentId(dept) || dept.name;
        counts[key] = activeBaseUsers.filter((user) =>
          userHasDepartment(user.departments, key)
        ).length;
      });
      return counts;
    }, [activeBaseUsers, filterDepartments]);

    const membershipCounts = useMemo(() => {
      const counts = {};
      availableMemberships.forEach((mem) => {
        if (isOffRosterMembership(mem)) return;
        counts[mem] = activeBaseUsers.filter((user) => user.membershipType === mem).length;
      });
      return counts;
    }, [activeBaseUsers]);

    // Clear filtered users when semester changes
    useEffect(() => {
        if (semester?._id) {
            setFilteredUsers([]);
        }
    }, [semester?._id, setSelectedDepartments, setSelectedMemberships]);

    useEffect(() => {
        if (semesterUsersLoaded) {
            const filtered = users.filter((user) =>
                userMatchesRosterFilters(user, {
                    searchQuery,
                    selectedDepartments,
                    selectedMemberships,
                })
            );
            setFilteredUsers(filtered);
        }
    }, [searchQuery, selectedDepartments, selectedMemberships, semesterUsersLoaded, users]);

    // Update paginatedUsers calculation:
    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * usersPerPage;
        const endIndex = startIndex + usersPerPage;
        return sortedActiveUsers.slice(startIndex, endIndex);
    }, [sortedActiveUsers, currentPage, usersPerPage]);

    const debouncedSearch = useCallback(
        (query) => {
            setSearchQuery(query);
            setCurrentPage(1);
        },
        [setCurrentPage]
    );

    // 3. Add useEffect to hydrate state from URL on load
     useEffect(() => {
       const params = new URLSearchParams(location.search);
       const q = params.get('q') || '';
       const deps = params.get('departments');
       const mems = params.get('memberships');
       const page = parseInt(params.get('page') || '1', 10);

       setSearchQuery(q);
       setSelectedDepartments(deps ? resolveDepartmentFilterValues(deps.split(','), catalog) : []);
       setSelectedMemberships(mems ? mems.split(',') : []);
       setCurrentPage(Number.isNaN(page) ? 1 : page);

       if (searchInputRef.current) {
         searchInputRef.current.value = q;
       }
     // eslint-disable-next-line react-hooks/exhaustive-deps
     }, []); // Runs once on mount

     // 4. Add useEffect to update URL when filters change
     useEffect(() => {
       const params = new URLSearchParams();
       if (searchQuery) {
         params.set('q', searchQuery);
       }
       if (selectedDepartments.length > 0) {
         params.set('departments', selectedDepartments.join(','));
       }
       if (selectedMemberships.length > 0) {
         params.set('memberships', selectedMemberships.join(','));
       }
       if (currentPage > 1) {
         params.set('page', String(currentPage));
       }

       history.replace({
         pathname: location.pathname,
         search: params.toString()
       });
     }, [searchQuery, selectedDepartments, selectedMemberships, currentPage, history, location.pathname]);


    // useEffect hook dedicated to managing search input focus
    useEffect(() => {
        if (!loading && searchQuery && searchInputRef.current) {
            if (document.activeElement !== searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    }, [loading, searchQuery]);

    if (error) return <div>Error: {error.message}</div>;

    const handleSearchInputChange = (e) => {
        debouncedSearch(e.target.value);
    };


    // ... (Your handler and clearFilters functions remain the same)
    const clearFilters = () => {
      setSearchQuery('');
      setSelectedDepartments([]);
      setSelectedMemberships([]);
      setCurrentPage(1);
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
      }
    };

    return (
        <div className='page-container'>
            <NavHeader />

            <div className="page-title-row">
                <h1 className="page-title page-title-icon">
                    <span className="main-section-icon">
                        <span
                            className="main-section-icon-fill"
                            style={{ background: "rgba(122, 193, 67, 0.15)", color: "#7ac143" }}
                        >
                            <Icon icon="mdi:account-group" className="icon" />
                        </span>
                    </span>
                    Our Amazing Volunteers!
                </h1>

                {hasAnyPermission(contextUser, ['MANAGE_WAITING_APPROVAL_LIST', 'COMPARE_VOLUNTEERS', 'MANAGE_USERS']) && (
                    <div className="title-action-buttons">
                        {hasPermission(contextUser, 'MANAGE_WAITING_APPROVAL_LIST') && (
                            <Link
                                to={'/waiting-list'}
                                className="title-icon-button"
                                aria-label="Waiting for Approval"
                            >
                                <Icon icon="material-symbols:hourglass-empty" />
                            </Link>
                        )}

                        {hasPermission(contextUser, 'COMPARE_VOLUNTEERS') && (
                            <Link
                                to="/compare-sardines"
                                className="title-icon-button"
                                aria-label="Compare Sardines"
                            >
                                <Icon icon="material-symbols:compare-arrows" />
                            </Link>
                        )}

                        {hasPermission(contextUser, 'MANAGE_USERS') && (
                            <Link
                                to={'/users-bulk-edit'}
                                className="title-icon-button"
                                aria-label="Member Tracking"
                            >
                                <Icon icon="mdi:table-edit" />
                            </Link>
                        )}
                    </div>
                )}
            </div>

            {/* It is duplicated because of the mobile and desktop versions */}

            <div className='user-list-container users-page'>
                <div className='user-list-lateral-section'>
                    <div className="list-action-buttons">
                        {hasPermission(contextUser, 'MANAGE_WAITING_APPROVAL_LIST') && (
                            <Link
                                to={'/waiting-list'}
                                className='button list-button create-button icon-menu-button'
                                style={{ textDecoration: 'none' }}
                            >
                                <Icon icon="material-symbols:hourglass-empty" className="button-icon" />
                                <span className="button-label">Waiting for Approval</span>
                            </Link>
                        )}

                        {hasPermission(contextUser, 'COMPARE_VOLUNTEERS') && (
                            <Link
                                to="/compare-sardines"
                                className='button list-button sort-button icon-menu-button'
                                style={{ textDecoration: 'none' }}
                            >
                                <Icon icon="material-symbols:compare-arrows" className="button-icon" />
                                <span className="button-label">Compare Sardines</span>
                            </Link>
                        )}

                        {hasPermission(contextUser, 'MANAGE_USERS') && (
                            <Link
                                to={'/users-bulk-edit'}
                                className='button list-button icon-menu-button'
                                style={{ textDecoration: 'none' }}
                            >
                                <Icon icon="mdi:table-edit" className="button-icon" />
                                <span className="button-label">Member Tracking</span>
                            </Link>
                        )}
                    </div>

                    <div className='search-notification-layout' style={{ margin: '10px 0' }}>
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search ESNers..."
                                value={searchQuery}
                                onChange={handleSearchInputChange}
                                ref={searchInputRef}
                            />
                            <i className="uil uil-search search-icon"></i>
                        </div>
                    </div>

                    <div className="filter-container" style={{ margin: "10px 0" }}>
                        <div className="filter-section" id="department-filter-section">
                            <h3 className="filter-title">Departments</h3>
                            <div className="filters">
                                {selectedDepartments.length > 0 && (
                                    <button
                                        type="button"
                                        className="filter-clear-round"
                                        onClick={() => setSelectedDepartments([])}
                                        aria-label="Clear department filters"
                                    >
                                        <Icon icon="mdi:close" />
                                    </button>
                                )}
                                {filterDepartments.map((dept) => {
                                    const filterValue = getDepartmentId(dept) || dept.name;
                                    return (
                                    <button
                                        key={filterValue}
                                        className={`filter-button ${selectedDepartments.includes(filterValue) ? 'selected' : ''
                                            }`}
                                        onClick={() => handleDepartmentChange(filterValue)}
                                        style={{
                                            '--department-color': getDepartmentColor(dept, catalog)
                                        }}
                                    >
                                        <div className="department-icon">
                                            {getDepartmentIcon(dept, {}, catalog)}
                                        </div>
                                        <span className="department-name">{dept.name}</span>
                                        <span className="filter-count"> {departmentCounts[filterValue] || 0}</span>
                                    </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="filter-section" id="membership-filter-section">
                            <h3 className="filter-title">Membership Status</h3>
                            <div className="filters">
                                {selectedMemberships.length > 0 && (
                                    <button
                                        type="button"
                                        className="filter-clear-round"
                                        onClick={() => setSelectedMemberships([])}
                                        aria-label="Clear membership filters"
                                    >
                                        <Icon icon="mdi:close" />
                                    </button>
                                )}
                                {availableMemberships.map((mem) => (
                                    <button
                                    key={mem}
                                    className={`filter-button ${selectedMemberships.includes(mem) ? 'selected' : ''}`}
                                    onClick={() => handleMembershipChange(mem)}
                                    style={{ '--membership-color': membershipColors[mem] }}
                                    >
                                    <div className="membership-icon">{membershipIcons[mem]}</div>
                                    <span className="membership-name">{mem}</span>
                                    {(() => {
                                        if (mem === 'Unbound') {
                                            if (searchingUnbound && !unboundLoading) {
                                                return (
                                                    <span className="filter-count"> {unboundUsers.length}</span>
                                                );
                                            }
                                            return null;
                                        }
                                        if (mem === 'Alumni') {
                                            if (searchingAlumni && !alumniLoading) {
                                                return (
                                                    <span className="filter-count"> {alumniUsers.length}</span>
                                                );
                                            }
                                            return null;
                                        }
                                        return (
                                            <span className="filter-count"> {membershipCounts[mem] || 0}</span>
                                        );
                                    })()}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {(searchQuery || selectedDepartments.length > 0 || selectedMemberships.length > 0) && (
                        <button
                            className="button list-button clear-button"
                            onClick={clearFilters}
                        >
                            <Icon icon="material-symbols:clear-all" className="button-icon" />
                            Clear Filters
                        </button>
                    )}
                </div>
                <div className='user-list-content-section'>
                    <UsersListSection
                        loading={loading || !semester?._id}
                        hasUsers={users.length > 0}
                        paginatedUsers={paginatedUsers}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        goToNextPage={goToNextPage}
                        goToPreviousPage={goToPreviousPage}
                        totalActiveCount={totalActiveUsers}
                        catalog={catalog}
                        emptyMessage={
                            onlyOffRosterFilters
                                ? 'Unbound and alumni members are listed in the sections below.'
                                : 'No users match your search criteria'
                        }
                    />
                    {!(loading && users.length === 0) && semester?._id && (
                        <>
                            <UsersListGroupSection
                                title="Suspended Members"
                                icon="tabler:fish-bone-filled"
                                users={sortedSuspendedUsers}
                                catalog={catalog}
                            />
                            {searchingUnbound && (
                                <UsersListGroupSection
                                    title="Members Who Left"
                                    icon="mdi:account-off-outline"
                                    users={sortedLeftUsers}
                                    catalog={catalog}
                                    defaultExpanded
                                />
                            )}
                            {searchingAlumni && (
                                <UsersListGroupSection
                                    title="Alumni"
                                    icon="tdesign:wave-bye"
                                    users={sortedAlumniUsers}
                                    catalog={catalog}
                                    defaultExpanded
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default UsersList;
